import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';

/*
 * Figma component "Input" → Field: 52 px, radius 18, bg/page, 1 px border/subtle, 16 px padding,
 * DLZ/Body/M; placeholder text/tertiary.
 *   hover    border/hover          focus  2 px border/focus
 *   error    2 px border/brand     disabled  bg/disabled, text/disabled
 * The 2 px states are an inset shadow on top of the 1 px border, so the text never moves.
 * `filled` is the core default – both variants look the same in this design.
 */
const inputVariants = cva(
  cn(
    'dlz-body-m relative flex h-[52px] w-full items-center border border-[#E2E4E7] bg-white text-[#0F1012] transition-[border-color,box-shadow]',
    'hover:border-[#9096A0] focus-within:border-[#0F1012] focus-within:shadow-[inset_0_0_0_1px_#0F1012]',
    'has-[:disabled]:cursor-not-allowed has-[:disabled]:border-[#E2E4E7] has-[:disabled]:bg-[#ECEDEF] has-[:disabled]:text-[#9096A0]',
  ),
  {
    variants: {
      variant: {
        outlined: 'ea-input__outlined',
        filled: 'ea-input__filled',
      },
      rounded: {
        default: ['rounded-[18px]', 'ea-input__wrapper--rounded-default'],
        none: ['rounded-none', 'ea-input__wrapper--rounded-none'],
      },
    },
    defaultVariants: {
      rounded: 'default',
      variant: 'filled',
    },
  },
);

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'>,
    VariantProps<typeof inputVariants> {
  /** Content inside the field on the left (icon 20 px). */
  before?: React.ReactNode;
  /** Content inside the field on the right (status icon, button). */
  after?: React.ReactNode;

  beforeOuter?: React.ReactNode;
  afterOuter?: React.ReactNode;

  className?: {
    wrapper?: string;
    main?: string;
    before?: string;
    after?: string;
  };
  classNames?: {
    wrapper?: string;
    main?: string;
    before?: string;
    after?: string;
  };
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      before,
      after,
      afterOuter,
      beforeOuter,
      classNames,
      rounded,
      variant,
      ...props
    },
    ref,
  ) => {
    return (
      <>
        {beforeOuter}
        <div
          className={cn(
            inputVariants({ rounded, variant }),
            'ea-input__wrapper',
            props['aria-invalid'] &&
              'ea-input__wrapper--invalid border-[#D7191F] shadow-[inset_0_0_0_1px_#D7191F] focus-within:border-[#D7191F] focus-within:shadow-[inset_0_0_0_1px_#D7191F] hover:border-[#D7191F]',
            className?.wrapper,
            classNames?.wrapper,
          )}
        >
          {before ? (
            <div
              className={cn(
                'pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-[#6B7079]',
                className?.before,
                classNames?.before,
              )}
            >
              {before}
            </div>
          ) : null}
          <input
            type={type}
            className={cn(
              'h-full w-full min-w-0 flex-1 rounded-[inherit] bg-transparent px-4 outline-none placeholder:text-[#6B7079] disabled:cursor-not-allowed',
              'file:mr-3 file:h-full file:cursor-pointer file:border-none file:bg-[#F5F6F7] file:text-sm file:font-semibold',
              !!before && 'pl-11',
              !!after && 'pr-11',
              'ea-input__main',
              className?.main,
              classNames?.main,
            )}
            ref={ref}
            {...props}
          />
          {after ? (
            <div
              className={cn(
                'absolute inset-y-0 right-0 flex items-center pr-4',
                className?.after,
                classNames?.after,
              )}
            >
              {after}
            </div>
          ) : null}
        </div>
        {afterOuter}
      </>
    );
  },
);
Input.displayName = 'Input';

export { Input };
